import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Button, IconButton, Drawer, List, ListItem, useMediaQuery, Dialog, DialogContent, DialogActions, DialogTitle, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import ClearIcon from '@mui/icons-material/Clear';
import LoginIcon from '@mui/icons-material/Login';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, provider } from '../auth/firebaseConfig'; 
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import ProfileAvatar from '../ProfileAvatar';
import { Box } from '@mui/material';
import { setCookie, getCookie, clearAllCookies } from '../../utils/cookieUtils';
import ReCaptcha from '../ReCaptcha';
import {
  ensureSuperAdminUserRecord,
  fetchTenantBranding,
  loginWithUserManagementApi
} from '../../DatabaseApis/DBQuery';
import { db } from '../auth/firebaseConfig';
import { getSuperAdminEmails } from '../../utils/superAdminUtils';
import { buildCcsPricingUrl } from '../../utils/ccsNavigation';

// import {jwtDecode} from 'jwt-decode';
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(0.5, 2), // Reduced padding from 1 to 0.5
  transition: 'background-color 0.3s ease',
  position: 'fixed',
  top: 0,
  width: '100%',
  zIndex: theme.zIndex.appBar,
}));

const Logo = styled('img')(({ isMobile }) => ({
  height: '65px',
  marginRight: isMobile ? 'auto' : '16px',
  marginLeft: isMobile ? 'auto' : '0',
  cursor: 'pointer',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '20px',
  padding: theme.spacing(1),
  margin: theme.spacing(0, 1),
  color: '#333',
  backgroundColor: 'transparent',
  transition: 'background-color 0.3s, color 0.3s, transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    backgroundColor: '#f0f0f0',
    color: '#1976d2',
    transform: 'scale(1.05)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
  },
}));

const ProjectBoardNames = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Roboto, sans-serif',
  color: '#333',
}));

const Navbar = ({ boardName, prjName, tenantLogoUrl, tenantOrganizationName }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [recaptchaDialogOpen, setRecaptchaDialogOpen] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [tenantChoiceOpen, setTenantChoiceOpen] = useState(false);
  const [tenantChoices, setTenantChoices] = useState([]);
  const [selectedTenantChoice, setSelectedTenantChoice] = useState('');
  const [pendingLoginContext, setPendingLoginContext] = useState(null);
  const [resolvedTenantLogoUrl, setResolvedTenantLogoUrl] = useState('');
  const [resolvedTenantOrganizationName, setResolvedTenantOrganizationName] = useState('');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  useEffect(() => {
    if (location.pathname === '/') {
      // Landing page should always behave as logged out
      setIsAuthenticated(false);
      clearAllCookies();
      signOut(auth).catch(() => {});
      return;
    }

    const userData = getCookie('userData');
    if (userData) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe();
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const shouldOpenSignIn = new URLSearchParams(location.search).get('signIn') === '1';
    if (!shouldOpenSignIn) return;

    setRecaptchaDialogOpen(true);
    navigate('/', { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadTenantBranding = async () => {
      if (location.pathname === '/') return;

      const userData = getCookie('userData');
      const tenantId = userData?.tenantId;

      if (!tenantId) {
        if (isMounted) {
          setResolvedTenantLogoUrl('');
          setResolvedTenantOrganizationName('');
        }
        return;
      }

      // Prefer explicit props from pages like ProjectPage, but backfill globally elsewhere.
      if (tenantLogoUrl || tenantOrganizationName) {
        if (isMounted) {
          setResolvedTenantLogoUrl(tenantLogoUrl || '');
          setResolvedTenantOrganizationName(tenantOrganizationName || '');
        }
        return;
      }

      try {
        if (!isMounted) return;
        const tenantBranding = await fetchTenantBranding(tenantId);
        if (!isMounted) return;

        setResolvedTenantLogoUrl(tenantBranding.companyLogoUrl || '');
        setResolvedTenantOrganizationName(tenantBranding.organizationName || tenantId || 'Organization');
      } catch (error) {
        console.error('Error loading tenant branding for navbar:', error);
      }
    };

    loadTenantBranding();
    return () => {
      isMounted = false;
    };
  }, [location.pathname, tenantLogoUrl, tenantOrganizationName]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      clearAllCookies();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleRecaptchaVerify = (token) => {
    setRecaptchaToken(token);
    setRecaptchaDialogOpen(false);
    if (token) {
      handleSignInWithGoogle();
    }
  };

  const handleSignInClick = () => {
    setRecaptchaDialogOpen(true);
  };

  const handlePricingClick = () => {
    window.location.assign(buildCcsPricingUrl());
  };

  const handleSignInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      // Ensure auth token/claims are available before Firestore reads.
      await user.getIdToken();
      const isProd = process.env.REACT_APP_STAGE === "PROD";
      const superAdmins = getSuperAdminEmails();

      // First check if user is a superadmin
      if (superAdmins.includes(user.email.toLowerCase())) {
        try {
          await ensureSuperAdminUserRecord(user);
        } catch (error) {
          console.error('Error creating superadmin user record:', error);
        }
        
        if(isProd || !process.env.REACT_APP_STAGE){
          const loginData = {
            email: user.email,
            recaptchaToken: recaptchaToken
          };

          const userData = await loginWithUserManagementApi(loginData);
          setCookie('userData', userData);
        } else {
          const token = await user.getIdToken();
          const email = user.email;
          const sessionData = {
            email,
            token,
            recaptchaToken
          };
          setCookie('userData', sessionData);
        }
        navigate('/tenant-page');
        return;
      }

      const emailLower = user.email.toLowerCase();
      const completeUserLogin = async (selectedUserData) => {
        const effectiveRole = selectedUserData?.role || 'Admin';
        
        // Set cookie and navigate
        if(isProd || !process.env.REACT_APP_STAGE){
          const loginData = {
            email: user.email,
            tenantId: selectedUserData?.tenantId || "1234",
            userRole: effectiveRole,
            recaptchaToken: recaptchaToken
          };

          const userData = await loginWithUserManagementApi(loginData);
          setCookie('userData', userData);
        } else {
          const token = await user.getIdToken();
          const email = user.email;
          const sessionData = {
            email,
            token,
            recaptchaToken,
            userId: selectedUserData?.userId || user.uid,
            userName: selectedUserData?.userName || user.displayName || user.email,
            tenantId: selectedUserData?.tenantId,
            role: effectiveRole
          };

          console.log('Setting session data for user:', sessionData);
          setCookie('userData', sessionData);
        }

        // Navigate to project page
        navigate('/project-page');
      };

      const fallbackMembership = async () => {
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult?.claims || {};
        return {
          userId: user.uid,
          email: user.email,
          userName: user.displayName || user.email,
          tenantId: claims.tenantId || "1234",
          role: claims.role || 'Admin',
          assignedGroups: [],
          tenantName: claims.tenantId || "Default Tenant",
        };
      };

      // Primary auth source is users collection (allow multiple memberships).
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", emailLower)
      );
      let usersSnapshot;
      try {
        usersSnapshot = await getDocs(usersQuery);
      } catch (error) {
        if (error?.code === 'permission-denied') {
          console.warn('users query denied; using token-based fallback session', error);
          await completeUserLogin(await fallbackMembership());
          return;
        }
        throw error;
      }

      // Legacy fallback: migrate old tenantAdmins-only entries on sign-in.
      if (usersSnapshot.empty) {
        try {
          const { addDoc, updateDoc, doc } = await import('firebase/firestore');
          const tenantAdminsQuery = query(
            collection(db, "tenantAdmins"),
            where("adminEmail", "==", emailLower)
          );
          const adminSnapshot = await getDocs(tenantAdminsQuery);
          if (!adminSnapshot.empty) {
            const legacyAdmin = adminSnapshot.docs[0].data();
            const newUserRef = await addDoc(collection(db, "users"), {
              email: emailLower,
              tenantId: legacyAdmin.tenantId || '',
              userName: legacyAdmin.adminName || user.displayName || user.email,
              role: 'Admin',
              userId: '',
              assignedGroups: [],
            });
            await updateDoc(doc(db, "users", newUserRef.id), { userId: newUserRef.id });
            usersSnapshot = await getDocs(usersQuery);
          }
        } catch (error) {
          console.error('Error while migrating legacy tenant admin user:', error);
        }
      }

      if (usersSnapshot.empty) {
        console.error('No user record found for signed-in email:', user.email);
        return;
      }

      let tenantNameMap = new Map();
      let tenantCollectionReadable = false;
      let tenantDocs = [];
      try {
        const allTenantsSnapshot = await getDocs(collection(db, 'tenant'));
        tenantCollectionReadable = true;
        tenantDocs = allTenantsSnapshot.docs;
        tenantNameMap = new Map(
          tenantDocs.map((docSnap) => [docSnap.data()?.tenantId, docSnap.data()?.tenantName || 'Tenant'])
        );
      } catch (error) {
        if (error?.code !== 'permission-denied') {
          throw error;
        }
        console.warn('tenant collection read denied during sign-in; continuing without tenant names', error);
      }

      const membershipMap = new Map();
      usersSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const tenantId = (data.tenantId || '').trim();
        if (!tenantId) return;
        if (!membershipMap.has(tenantId)) {
          membershipMap.set(tenantId, {
            id: docSnap.id,
            userId: data.userId || docSnap.id,
            email: data.email || emailLower,
            userName: data.userName || user.displayName || user.email,
            role: data.role || 'Admin',
            assignedGroups: data.assignedGroups || [],
            tenantId,
            tenantName: tenantNameMap.get(tenantId) || tenantId,
          });
        }
      });

      // CCS-created tenants may reach OVplan before their user membership row
      // is available. Tenant admin fields are still authoritative enough to
      // let the assigned admin choose that tenant at sign-in.
      if (tenantCollectionReadable) {
        tenantDocs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const tenantId = (data.tenantId || docSnap.id || '').trim();
          if (!tenantId || membershipMap.has(tenantId)) return;

          const tenantAdminEmails = [
            ...(Array.isArray(data.adminEmails) ? data.adminEmails : []),
            ...(Array.isArray(data.admins) ? data.admins : []),
            data.adminEmail,
            data.ownerEmail,
          ]
            .map((value) => String(value || '').trim().toLowerCase())
            .filter(Boolean);

          if (!tenantAdminEmails.includes(emailLower)) return;

          membershipMap.set(tenantId, {
            id: docSnap.id,
            userId: '',
            email: emailLower,
            userName: user.displayName || user.email,
            role: 'Admin',
            assignedGroups: [],
            tenantId,
            tenantName: data.tenantName || tenantNameMap.get(tenantId) || tenantId,
          });
        });
      }

      let memberships = Array.from(membershipMap.values());

      // If we can read live tenant docs, remove stale user memberships for deleted tenants.
      if (tenantCollectionReadable) {
        memberships = memberships.filter((membership) => tenantNameMap.has(membership.tenantId));
      }

      if (memberships.length === 0) {
        if (tenantCollectionReadable) {
          console.warn('User memberships exist but mapped tenants are deleted/missing; no tenant choices available');
          return;
        }
        console.warn('No tenant membership found in users; using token-based fallback session');
        await completeUserLogin(await fallbackMembership());
        return;
      }

      if (memberships.length === 1) {
        await completeUserLogin(memberships[0]);
        return;
      }

      // Multiple tenant memberships: user must choose from only their own tenants.
      setTenantChoices(memberships);
      setSelectedTenantChoice(memberships[0].tenantId);
      setPendingLoginContext({ completeUserLogin });
      setTenantChoiceOpen(true);
    } catch (error) {
      console.error('Error during sign in:', error?.code, error?.message, error);
    }
  };

  const handleTenantChoiceConfirm = async () => {
    if (!pendingLoginContext || !selectedTenantChoice) return;
    const selectedMembership = tenantChoices.find((item) => item.tenantId === selectedTenantChoice);
    if (!selectedMembership) return;
    try {
      await pendingLoginContext.completeUserLogin(selectedMembership);
    } finally {
      setTenantChoiceOpen(false);
      setTenantChoices([]);
      setSelectedTenantChoice('');
      setPendingLoginContext(null);
    }
  };

  const handleTenantChoiceCancel = async () => {
    setTenantChoiceOpen(false);
    setTenantChoices([]);
    setSelectedTenantChoice('');
    setPendingLoginContext(null);
    clearAllCookies();
    try {
      await signOut(auth);
    } catch (e) {
      // Ignore sign-out errors here.
    }
  };

  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  const isLandingPage = currentPath === '/';
  const isPublicNavPage = ['/', '/pricing', '/checkout'].includes(currentPath);
  const showPricingNav = currentPath === '/';
  const showSignInNav = isPublicNavPage && !isAuthenticated;
  const publicHomePath = isAuthenticated ? '/project-page' : '/';
  const handlePublicHomeClick = () => navigate(publicHomePath);

  return (
    <StyledAppBar position="static">
      <Toolbar sx={{ 
        justifyContent: isMobile ? 'center' : 'flex-start',
        minHeight: '48px !important', // Force smaller height
        padding: '0 16px' // Reduce horizontal padding
      }}>
        {isMobile && (
          <>
            <IconButton
              edge="start"
              color="default"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ color: '#000', position: 'absolute', left: 10 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  width: '250px',
                  backgroundColor: '#fff',
                  color: '#333',
                },
              }}
            >
              <List>
                {isPublicNavPage ? (
                  <>
                    {!isLandingPage && (
                      <ListItem>
                        <StyledButton onClick={handlePublicHomeClick}>
                          Home
                        </StyledButton>
                      </ListItem>
                    )}
                    {showPricingNav && (
                      <ListItem>
                        <StyledButton onClick={handlePricingClick}>
                          Pricing
                        </StyledButton>
                      </ListItem>
                    )}
                    {showSignInNav && (
                      <ListItem>
                        <StyledButton onClick={handleSignInClick}>
                          <LoginIcon /> Sign In
                        </StyledButton>
                      </ListItem>
                    )}
                  </>
                ) : (
                   <>
                     {isAuthenticated ? (
                       <ListItem>
                         <StyledButton onClick={handleLogout}>
                           <LogoutIcon /> Sign Out
                         </StyledButton>
                       </ListItem>
                     ) : (
                       <ListItem>
                         <StyledButton onClick={handleSignInClick}>
                           <LoginIcon /> Sign In
                         </StyledButton>
                       </ListItem>
                     )}
                   </>
                 )}
                 <ListItem>
                   <StyledButton onClick={toggleDrawer(false)}>
                     <ClearIcon /> Close Menu
                   </StyledButton>
                 </ListItem>
              </List>
            </Drawer>
          </>
        )}

        {isPublicNavPage && (
          <Logo
            src="/logo.png"
            alt="OV Plan Logo"
            isMobile={isMobile}
            onClick={handlePublicHomeClick}
          />
        )}

<ProjectBoardNames>
  {prjName && boardName 
    ? `${prjName.slice(0, 15)}${prjName.length > 15 ? '...' : ''} / ${boardName.slice(0, 15)}${boardName.length > 15 ? '...' : ''}` 
    : ''}
</ProjectBoardNames>

        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            {isPublicNavPage && (
              <>
                {!isLandingPage && (
                  <StyledButton onClick={handlePublicHomeClick}>Home</StyledButton>
                )}
                {showPricingNav && (
                  <StyledButton onClick={handlePricingClick}>Pricing</StyledButton>
                )}
                {showSignInNav && (
                  <StyledButton onClick={handleSignInClick}>Sign In</StyledButton>
                )}
              </>
            )}
          </div>
         )}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 1.25 }}>
          {!isLandingPage && isAuthenticated ? (
            <ProfileAvatar
              tenantLogoUrl={tenantLogoUrl || resolvedTenantLogoUrl}
              tenantOrganizationName={tenantOrganizationName || resolvedTenantOrganizationName}
              showCreditsShortcut
              showCreditsLabel={false}
            />
          ) : null}
        </Box>

        <Dialog open={recaptchaDialogOpen} onClose={() => setRecaptchaDialogOpen(false)}>
          <DialogContent>
            <ReCaptcha onVerify={handleRecaptchaVerify} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecaptchaDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={tenantChoiceOpen} onClose={handleTenantChoiceCancel} maxWidth="xs" fullWidth>
          <DialogTitle>Select Tenant</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel id="tenant-choice-label">Tenant</InputLabel>
              <Select
                labelId="tenant-choice-label"
                label="Tenant"
                value={selectedTenantChoice}
                onChange={(e) => setSelectedTenantChoice(e.target.value)}
              >
                {tenantChoices.map((membership) => (
                  <MenuItem key={`${membership.tenantId}-${membership.id}`} value={membership.tenantId}>
                    {membership.tenantName} ({membership.tenantId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTenantChoiceCancel}>Cancel</Button>
            <Button onClick={handleTenantChoiceConfirm} variant="contained" disabled={!selectedTenantChoice}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Navbar;
